import React, { useEffect, useMemo, useState } from 'react';
import { RotateCcw, Trash2, FolderOpen, Folder, Sparkles, Bot } from 'lucide-react';
import Modal from '../components/Modal';
import { apiClient } from '../apiClient';

const TYPE_CONFIG = {
  projects: { label: 'Projects', icon: FolderOpen, type: 'project', idKey: 'project_id' },
  folders: { label: 'Folders', icon: Folder, type: 'folder', idKey: 'folder_id' },
  prompts: { label: 'Prompts', icon: Sparkles, type: 'prompt', idKey: 'prompt_id' },
  instruction_projects: { label: 'Instruction Projects', icon: FolderOpen, type: 'instruction_project', idKey: 'project_id' },
  instruction_folders: { label: 'Instruction Folders', icon: Folder, type: 'instruction_folder', idKey: 'folder_id' },
  instructions: { label: 'Agent Instructions', icon: Bot, type: 'instruction', idKey: 'instruction_id' },
};

const EMPTY_TRASH = {
  projects: [],
  folders: [],
  prompts: [],
  instruction_projects: [],
  instruction_folders: [],
  instructions: [],
  retention_days: 30
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const daysUntil = (value) => {
  if (!value) return '-';
  const expiry = new Date(value).getTime();
  if (Number.isNaN(expiry)) return '-';
  const diff = expiry - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

function TrashPage() {
  const [trash, setTrash] = useState(EMPTY_TRASH);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actioning, setActioning] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmPurge, setConfirmPurge] = useState(false);

  const totalItems = useMemo(
    () => Object.keys(TYPE_CONFIG).reduce((count, key) => count + (trash[key]?.length || 0), 0),
    [trash]
  );

  const loadTrash = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient.get('/api/trash');
      setTrash({
        projects: Array.isArray(data?.projects) ? data.projects : [],
        folders: Array.isArray(data?.folders) ? data.folders : [],
        prompts: Array.isArray(data?.prompts) ? data.prompts : [],
        instruction_projects: Array.isArray(data?.instruction_projects) ? data.instruction_projects : [],
        instruction_folders: Array.isArray(data?.instruction_folders) ? data.instruction_folders : [],
        instructions: Array.isArray(data?.instructions) ? data.instructions : [],
        retention_days: data?.retention_days || 30,
      });
    } catch (err) {
      setError(err.message || 'Unable to load trash.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrash();
  }, []);

  const handleRestore = async (resourceType, resourceId) => {
    setActioning(true);
    setError('');
    try {
      await apiClient.post('/api/trash/restore', { resource_type: resourceType, resource_id: resourceId });
      await loadTrash();
    } catch (err) {
      setError(err.message || 'Restore failed.');
    } finally {
      setActioning(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!confirmDelete) return;
    setActioning(true);
    setError('');
    try {
      await apiClient.post('/api/trash/delete', confirmDelete);
      setConfirmDelete(null);
      await loadTrash();
    } catch (err) {
      setError(err.message || 'Permanent delete failed.');
    } finally {
      setActioning(false);
    }
  };

  const handleDeleteAll = async () => {
    setActioning(true);
    setError('');
    try {
      await apiClient.post('/api/trash/delete-all', {});
      setConfirmPurge(false);
      await loadTrash();
    } catch (err) {
      setError(err.message || 'Unable to permanently delete all trash.');
    } finally {
      setActioning(false);
    }
  };

  return (
    <div className="min-h-screen projects-page">
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1 className="page-title">Trash</h1>
            <p className="page-subtitle">
              Deleted items stay here for {trash.retention_days || 30} days before auto-expiring.
            </p>
          </div>
          <button className="btn-secondary danger" onClick={() => setConfirmPurge(true)} disabled={totalItems === 0 || actioning}>
            <Trash2 className="w-4 h-4" /> Delete All Permanently
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {loading ? <div className="spinner"></div> : null}

      {!loading && totalItems === 0 && (
        <div className="empty-state card-glass">
          <h3 className="text-xl font-bold mb-2">Trash is empty</h3>
          <p className="text-gray-400">Deleted projects, folders, prompts, and agent instructions will appear here.</p>
        </div>
      )}

      {!loading && totalItems > 0 && (
        <div className="space-y-6">
          {Object.entries(TYPE_CONFIG).map(([key, config]) => {
            const items = trash[key] || [];
            const Icon = config.icon;
            return (
              <div className="card-glass" key={key}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    {config.label}
                  </h2>
                  <span className="badge">{items.length}</span>
                </div>
                {items.length === 0 ? (
                  <div className="text-sm text-gray-400">No {config.label.toLowerCase()} in trash.</div>
                ) : (
                  <div className="space-y-3">
                    {items.map((item) => {
                      const resourceId = item[config.idKey];
                      return (
                        <div className="trash-row" key={resourceId}>
                          <div className="trash-row-main">
                            <div className="trash-row-title">{item.name || 'Untitled'}</div>
                            <div className="trash-row-meta">
                              Trashed {formatDateTime(item.trashed_at)} | {daysUntil(item.trash_expires_at)} day(s) left
                            </div>
                          </div>
                          <div className="trash-row-actions">
                            <button
                              className="btn-secondary btn-compact"
                              onClick={() => handleRestore(config.type, resourceId)}
                              disabled={actioning}
                            >
                              <RotateCcw className="w-4 h-4" /> Restore
                            </button>
                            <button
                              className="btn-secondary danger btn-compact"
                              onClick={() => setConfirmDelete({ resource_type: config.type, resource_id: resourceId })}
                              disabled={actioning}
                            >
                              <Trash2 className="w-4 h-4" /> Delete Permanently
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={!!confirmDelete}
        title="Delete Permanently?"
        onClose={() => setConfirmDelete(null)}
        footer={(
          <div className="modal-actions">
            <button className="btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn-primary danger" onClick={handlePermanentDelete} disabled={actioning}>
              Delete Permanently
            </button>
          </div>
        )}
      >
        <p className="text-gray-300">This cannot be undone.</p>
      </Modal>

      <Modal
        open={confirmPurge}
        title="Delete All Trash Permanently?"
        onClose={() => setConfirmPurge(false)}
        footer={(
          <div className="modal-actions">
            <button className="btn-secondary" onClick={() => setConfirmPurge(false)}>Cancel</button>
            <button className="btn-primary danger" onClick={handleDeleteAll} disabled={actioning}>
              Delete All Permanently
            </button>
          </div>
        )}
      >
        <p className="text-gray-300">All trashed prompt and agent instruction workspace items will be removed forever.</p>
      </Modal>
    </div>
  );
}

export default TrashPage;
